import { msg } from "@lingui/core/macro";
import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { createRoute, Link, useLocation, useNavigate } from "@tanstack/solid-router";
import { AlertTriangle, Database, Loader2, LogOut, ShieldAlert, Trash2, Undo2 } from "@fleet/icons";
import { Card, CardContent, Label, PageContainer, PageHeader, TextField } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { appRoute } from "./route";
import { useI18n } from "@/providers/i18n-provider";
import { api, createApiQuery } from "@/lib/api";
import { useExecuteSignOut, useSession } from "@/lib/auth";
import { sharedApi } from "@jornada/shared";
import { emailMatchesAccount, shouldRedirectGuestAwayFromDeletion } from "@/utils/account-deletion";

/**
 * Dedicated DELETE ACCOUNT page (own URL, `/profile/delete-account`).
 *
 * Designed as an anti-accidental-deletion and anti-confusion-with-"sign-out" flow:
 *   - It is NOT an inline dialog in the profile; it is a linkable route that clearly
 *     explains what is lost (all data/lists, sign-out on every device, a 30-day grace
 *     period, then permanent deletion).
 *   - The destructive button opens a dialog requiring the account email to be
 *     RE-TYPED; "Delete my account" remains disabled until the text matches
 *     (trim + case-insensitive; see `utils/account-deletion.ts`).
 *   - If deletion is already pending, the page does NOT allow requesting it again: it
 *     shows the state and lets the user cancel/restore (the banner also lives in
 *     profile → danger-zone, the usual entry point when signing back in during
 *     the grace period).
 *   - Authenticated users only; a guest is redirected to the profile.
 *
 * The backend does NOT change: it reuses requestAccountDeletion / getDeletionStatus /
 * restoreAccount, identical to the previous danger-zone flow.
 */
function DeleteAccountPage() {
  const { t } = useI18n();
  const session = useSession();
  const navigate = useNavigate();
  const executeSignOut = useExecuteSignOut();

  const [dialogOpen, setDialogOpen] = createSignal(false);
  const [typedEmail, setTypedEmail] = createSignal("");
  const [busy, setBusy] = createSignal(false);

  const location = useLocation();
  const accountEmail = () => session.data?.user.email ?? "";

  // A guest has no account to delete: return them to the profile as soon as
  // boot/priming finishes (do not redirect while the session is still pending,
  // to avoid expelling a real user because of an in-flight verification).
  //
  // The pathname term is NOT decorative: it anchors the redirect to "WE ARE STILL
  // on the deletion page", so it fires ONCE. Without it, the destination
  // satisfies the condition again (a guest is still a guest) and the
  // effect re-fires. See `shouldRedirectGuestAwayFromDeletion`.
  createEffect(() => {
    if (
      shouldRedirectGuestAwayFromDeletion({
        isPending: session.isPending,
        isVisitor: session.isVisitor(),
        pathname: location().pathname,
      })
    ) {
      void navigate({ to: "/profile", replace: true });
    }
  });

  const statusQuery = createApiQuery(
    () => ["account-deletion-status"],
    async () => sharedApi.getDeletionStatus(api),
    () => ({ suspense: false }),
  );

  const purgeAt = () => statusQuery.data?.purgeAt ?? null;
  const pending = () => Boolean(purgeAt());
  const purgeDate = () => {
    const at = purgeAt();
    return at ? new Date(at).toLocaleDateString() : "";
  };

  const canConfirm = createMemo(() => emailMatchesAccount(typedEmail(), accountEmail()));

  const requestDeletion = () => {
    if (!canConfirm() || busy()) {
      return;
    }
    void (async () => {
      setBusy(true);
      try {
        await sharedApi.requestAccountDeletion(api);
        // The server has already closed all sessions: local cleanup + /login.
        executeSignOut();
      } finally {
        setBusy(false);
      }
    })();
  };

  const cancelDeletion = () => {
    void (async () => {
      setBusy(true);
      try {
        await sharedApi.restoreAccount(api);
        await statusQuery.refetch();
      } finally {
        setBusy(false);
      }
    })();
  };

  const consequences = () => [
    {
      icon: Database,
      title: t(msg`All your data is erased`),
      body: t(
        msg`Every site, inspection and setting tied to this account is permanently removed and cannot be recovered.`,
      ),
    },
    {
      icon: LogOut,
      title: t(msg`Signed out everywhere`),
      body: t(msg`You will be signed out on every device the moment the deletion is requested.`),
    },
    {
      icon: AlertTriangle,
      title: t(msg`30-day grace period, then permanent`),
      body: t(
        msg`You can still recover the account by signing in and cancelling within 30 days. After that, deletion is final.`,
      ),
    },
  ];

  return (
    <PageContainer maxWidth="2xl" class="space-y-8" containerClass="scrollbar-thin">
      <div data-testid="delete-account-page">
        <PageHeader
          title={t(msg`Delete account`)}
          description={t(msg`Permanently remove your account and all of its data.`)}
          backTo="/profile"
          icon={ShieldAlert}
        />
      </div>

      <Show
        when={!pending()}
        fallback={
          <Card class="border-destructive/30">
            <CardContent class="space-y-4 p-6">
              <div
                class="flex items-start gap-3 rounded-xl border border-destructive/20 bg-destructive/5 p-4"
                data-testid="delete-account-pending-banner"
              >
                <AlertTriangle class="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
                <div class="space-y-1">
                  <p class="text-sm font-bold text-destructive">
                    {t(msg`Account deletion scheduled`)}
                  </p>
                  <p class="text-xs text-muted-foreground">
                    {(() => {
                      const date = purgeDate();
                      return t(msg`Your account will be permanently deleted on ${date}.`);
                    })()}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                class="h-12 w-full gap-2 rounded-2xl"
                onClick={cancelDeletion}
                disabled={busy()}
                data-testid="delete-account-restore"
              >
                <Show when={!busy()} fallback={<Loader2 class="h-5 w-5 animate-spin" />}>
                  <Undo2 class="h-5 w-5" />
                </Show>
                {t(msg`Cancel deletion and keep my account`)}
              </Button>
              <Link
                to="/profile"
                class="block text-center text-sm font-medium text-muted-foreground hover:text-foreground"
                data-testid="delete-account-cancel"
              >
                {t(msg`Back to profile`)}
              </Link>
            </CardContent>
          </Card>
        }
      >
        {/* What you will lose */}
        <Card class="border-destructive/30">
          <CardContent class="space-y-5 p-6">
            <div class="flex items-center gap-2 text-destructive">
              <AlertTriangle class="h-5 w-5" />
              <h2 class="text-lg font-bold">{t(msg`This action cannot be undone`)}</h2>
            </div>
            <p class="text-sm text-muted-foreground">
              {t(
                msg`Deleting your account is permanent. Please read what happens before you continue.`,
              )}
            </p>
            <ul class="space-y-4">
              <For each={consequences()}>
                {(item) => (
                  <li class="flex items-start gap-3">
                    <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-destructive/10 text-destructive">
                      <item.icon class="h-5 w-5" />
                    </div>
                    <div class="space-y-0.5">
                      <p class="text-sm font-semibold">{item.title}</p>
                      <p class="text-xs text-muted-foreground">{item.body}</p>
                    </div>
                  </li>
                )}
              </For>
            </ul>
          </CardContent>
        </Card>

        {/* Actions */}
        <div class="flex flex-col gap-3">
          <Button
            variant="destructive"
            class="h-12 w-full gap-2 rounded-2xl"
            onClick={() => {
              setTypedEmail("");
              setDialogOpen(true);
            }}
            disabled={busy() || statusQuery.isPending}
            data-testid="delete-account-open-confirm"
          >
            <Trash2 class="h-5 w-5" />
            {t(msg`Delete my account`)}
          </Button>
          <Link
            to="/profile"
            class="flex h-12 w-full items-center justify-center rounded-2xl border border-border/20 text-sm font-semibold text-foreground/80 transition-colors hover:bg-secondary/20"
            data-testid="delete-account-cancel"
          >
            {t(msg`Cancel and go back`)}
          </Link>
        </div>
      </Show>

      {/* Email-confirmation dialog */}
      <Dialog open={dialogOpen()} onOpenChange={setDialogOpen} contentClass="max-w-[440px] p-6">
        <div class="space-y-5" data-testid="delete-account-confirm-dialog">
          <DialogClose
            class="absolute right-3 top-3 z-10 text-muted-foreground hover:text-foreground"
            aria-label={t(msg`Close`)}
            data-testid="delete-account-confirm-close"
          />
          <div class="flex items-center gap-2 pr-8 text-destructive">
            <ShieldAlert class="h-6 w-6" />
            <DialogTitle class="text-xl font-bold">{t(msg`Confirm account deletion`)}</DialogTitle>
          </div>
          <DialogDescription class="text-sm text-muted-foreground">
            {t(
              msg`To confirm, type your account email below. Your account will be scheduled for permanent deletion and you will be signed out everywhere.`,
            )}
          </DialogDescription>

          <div class="space-y-2">
            <Label for="delete-account-email-input" class="text-xs font-semibold">
              {(() => {
                const email = accountEmail();
                return t(msg`Type ${email} to confirm`);
              })()}
            </Label>
            <TextField
              id="delete-account-email-input"
              type="email"
              autocomplete="off"
              autocapitalize="none"
              spellcheck={false}
              placeholder={accountEmail()}
              class="placeholder:text-muted-foreground/45 placeholder:italic"
              value={typedEmail()}
              onInput={(e) => {
                setTypedEmail(e.currentTarget.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canConfirm()) {
                  e.preventDefault();
                  requestDeletion();
                }
              }}
              data-testid="delete-account-email-input"
            />
          </div>

          <div class="flex justify-end gap-3 pt-1">
            <Button
              variant="ghost"
              class="h-11 rounded-xl px-5 font-bold"
              onClick={() => {
                setDialogOpen(false);
              }}
              disabled={busy()}
            >
              {t(msg`Cancel`)}
            </Button>
            <Button
              variant="destructive"
              class="h-11 min-w-[140px] rounded-xl px-6 font-bold"
              onClick={requestDeletion}
              disabled={!canConfirm() || busy()}
              data-testid="delete-account-confirm-button"
            >
              <Show when={!busy()} fallback={<Loader2 class="h-4 w-4 animate-spin" />}>
                <Trash2 class="mr-1 h-4 w-4" />
              </Show>
              {t(msg`Delete my account`)}
            </Button>
          </div>
        </div>
      </Dialog>
    </PageContainer>
  );
}

export const profileDeleteAccountRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/profile/delete-account",
  component: DeleteAccountPage,
});
